import * as React from 'react';

// Constants & Enums
import {
  FacebookAuthProvider,
  FACEBOOK_OAUTH_BASE_URL,
  FACEBOOK_OAUTH_SCOPES,
} from './constants';

// Utils
import { isWebBridgeSupported } from '@shopee-user/platform';
import { createTobSdkEventCallbackListener } from '../../../web-bridge-listener';
import { appendQueryToUrl } from '@shopee/url-util';
import { getWebThirdPartyClientId } from '@shopee-user/business-customization-logic';
import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';
import {
  generateCodeVerifier,
  generateCodeChallenge,
} from '@tob/shared/security/pkce';
import { createEncryptedState } from '@tob/shared/security/oauth-state';
import {
  isFeatureEnabled as isStaticFeatureEnabled,
  Feature as StaticFeature,
} from '@shopee-user/static-feature-toggle';
import { setThirdPartyOAuthContextToStorage } from '@tob/pages/third-party-service/sessionStorage';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';
import {
  setCurrentUrl,
  replaceCurrentUrl,
} from '@tob/shared/utils/redirection';
import { AuthMethod } from '@shopee/web-bridge-sdk';
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';

export const useConnectWithFacebook = ({
  redirectUri,
  entryPoint,
  shouldReplaceCurrentUrl = false,
  webViewAuthWith3PServiceByToken,
}: {
  redirectUri: string;
  entryPoint: ThirdpartyBindingEntryPoint;
  shouldReplaceCurrentUrl?: boolean;
  webViewAuthWith3PServiceByToken?: WithUserWebViewProps['webViewAuthWith3PServiceByToken'];
}) => {
  const tobSdkEventListener = React.useRef<ReturnType<
    typeof createTobSdkEventCallbackListener
  > | null>(null);

  React.useEffect(() => {
    return () => {
      if (tobSdkEventListener.current) {
        tobSdkEventListener.current.clear();
      }
    };
  }, []);

  const authenticate = async (): Promise<{
    authProvider: FacebookAuthProvider;
    authCode?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (isWebBridgeSupported()) {
      const result = await authenticateByFacebookApp();
      return {
        ...result,
        authProvider: FacebookAuthProvider.FacebookApp,
      };
    } else {
      await authenticateByFacebookWeb();
      return {
        authProvider: FacebookAuthProvider.FacebookWeb,
      };
    }
  };

  const authenticateByFacebookApp = async (): Promise<{
    authCode?: string;
    appClientId?: string;
    isCancel?: boolean;
    error?: number;
    errorMsg?: string;
  }> => {
    if (!webViewAuthWith3PServiceByToken) {
      return {
        error: 1,
        errorMsg: 'webViewAuthWith3PServiceByToken is not available',
      };
    }

    try {
      const response = await webViewAuthWith3PServiceByToken({
        method: AuthMethod.Facebook,
      });

      if (response.errorCode !== 0) {
        return {
          error: response.errorCode,
          errorMsg: response.errorMessage,
        };
      }

      if (response.authToken) {
        return {
          authCode: response.authToken,
          appClientId: response.serverClientId,
        };
      }

      return { isCancel: true };
    } catch (err) {
      return {
        error: 1,
        errorMsg: err instanceof Error ? err.message : String(err),
      };
    }
  };

  const authenticateByFacebookWeb = async () => {
    const isPkceEnabled = isStaticFeatureEnabled(
      StaticFeature.EnablePKCEParams,
      true
    );

    const clientId = getWebThirdPartyClientId({
      channel: AuthenticateChannel.Facebook,
    });

    const codeVerifier = isPkceEnabled ? generateCodeVerifier({}) : '';
    const codeChallenge = isPkceEnabled
      ? await generateCodeChallenge({ codeVerifier })
      : '';
    const { state, keyJwk } = await createEncryptedState({
      type: AuthenticateChannel.Facebook,
    });

    setThirdPartyOAuthContextToStorage({
      ...(isPkceEnabled ? { codeVerifier } : {}),
      oauthState: state,
      oauthStateKey: keyJwk,
      clientId,
      triggerUrl: window.location.href,
      entryPoint,
    });

    const authUrlParams: Record<string, string> = {
      scope: FACEBOOK_OAUTH_SCOPES.join(' '),
      response_type: 'code',
      state,
      redirect_uri: redirectUri,
      client_id: clientId,
    };
    if (isPkceEnabled) {
      authUrlParams.code_challenge = codeChallenge;
      authUrlParams.code_challenge_method = 'S256';
    }
    const authUrl = appendQueryToUrl(FACEBOOK_OAUTH_BASE_URL, authUrlParams);
    if (shouldReplaceCurrentUrl) {
      replaceCurrentUrl({ url: authUrl, isHardcodedUrl: true });
    } else {
      setCurrentUrl({ url: authUrl, isHardcodedUrl: true });
    }
    return {};
  };

  return {
    authenticate,
  };
};
